import { defineStore } from 'pinia';
import { ref, watch, computed } from 'vue';
import { format } from 'date-fns';
import type {
  CourseData,
  CourseCategory,
  DayFilter,
  Course,
} from '../types/course';
import { fetchCourses } from '../services/api';
import {
  loadCourseSelections,
  saveCourseSelections,
} from '../utils/storageUtils';
import { mergeCourseData, getDayDate } from '../utils/courseUtils';
import { findOverlappingCourses } from '../utils/timeUtils';

// Flag to track if courses have been fetched
let coursesInitialized = false;

export const useCourseStore = defineStore('courses', () => {
  const courseData = ref<CourseData>({
    asanas: [],
    meditation: [],
    'other offerings': [],
  });
  const selectedCategory = ref<CourseCategory>('asanas');
  const selectedDay = ref<DayFilter>('today');
  const menuOpen = ref(false);
  const pastCourses = ref<Course[]>(loadPastCourses());
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  // Store overlapping IDs as an array instead of a Set
  const overlappingIds = ref<string[]>([]);
  // Add a new variable to control room modal functionality
  const roomModalEnabled = ref(false);
  // Add maintenance mode flag
  const maintenanceMode = ref(false);
  const maintenanceMessage = ref("Unser System befindet sich gerade in der Wartung, bitte später nochmal versuchen");

  function loadPastCourses(): Course[] {
    const stored = localStorage.getItem('yoga-past-courses');
    return stored ? JSON.parse(stored) : [];
  }

  const CONDUCTOR_TTL = 5 * 60 * 60 * 1000; // 5 hours in ms

  const showConductors = ref(loadShowConductors());

  function loadShowConductors(): boolean {
    const stored = localStorage.getItem('yoga-show-conductors');
    if (!stored) return false;
    const timestamp = parseInt(stored, 10);
    if (isNaN(timestamp)) return false;
    return Date.now() - timestamp < CONDUCTOR_TTL;
  }

  // Watch for changes in courseData and save to localStorage
  watch(
    courseData,
    (newData) => {
      saveCourseSelections(newData);
    },
    { deep: true }
  );

  watch(
    pastCourses,
    (newCourses) => {
      localStorage.setItem('yoga-past-courses', JSON.stringify(newCourses));
    },
    { deep: true }
  );

  watch(
    showConductors,
    (val) => {
      if (val) {
        localStorage.setItem('yoga-show-conductors', String(Date.now()));
      } else {
        localStorage.removeItem('yoga-show-conductors');
      }
    }
  );

  async function fetchAndUpdateCourses() {
    // Only fetch courses if they haven't been initialized yet
    if (coursesInitialized) {
      console.log('Courses already initialized, skipping fetch');
      return;
    }

    try {
      isLoading.value = true;
      error.value = null;
      maintenanceMode.value = false;
      console.log('Fetching courses...');
      const newCourseData = await fetchCourses();
      const savedSelections = loadCourseSelections();
      courseData.value = mergeCourseData(newCourseData, savedSelections);
      updateOverlappingCourses(); // Update overlapping courses after data changes
      
      // Mark courses as initialized
      coursesInitialized = true;
    } catch (e) {
      console.error('Error fetching courses:', e);
      
      // Always set maintenance mode on any error
      maintenanceMode.value = true;
      
      // Use saved data if available
      const savedSelections = loadCourseSelections();
      if (savedSelections) {
        courseData.value = savedSelections;
      }
    } finally {
      isLoading.value = false;
    }
  }

  function updateOverlappingCourses() {
    const selectedCourses = getAllSelectedCourses.value;
    const overlapping = findOverlappingCourses(selectedCourses);
    overlappingIds.value = Array.from(overlapping);
  }

  const toggleCourseSelection = (course: Course) => {
    course.selected = !course.selected;
    updateOverlappingCourses();
    // Explicitly save course selections after toggling
    saveCourseSelections(courseData.value);
  };

  const getSelectedCoursesCount = () => {
    let relevant_dates = [
      new Date(),
      new Date().setDate(new Date().getDate() + 1),
      new Date().setDate(new Date().getDate() + 2),
    ].map((d) => format(d, 'yyyy-MM-dd'));
    // let relevant_dates = [new Date().setDate(new Date().getDate() + 1), new Date().setDate(new Date().getDate() + 2), new Date().setDate(new Date().getDate() + 3)].map(d => format(d, 'yyyy-MM-dd'));
    return Object.values(courseData.value)
      .flat()
      .filter(
        (course) => course.selected && relevant_dates.includes(course.date)
      ).length;
  };

  const getSelectedCoursesCountForDay = (day: DayFilter) => {
    return Object.values(courseData.value)
      .flat()
      .filter((course) => course.selected && course.date === getDayDate(day))
      .length;
  };

  const getAllSelectedCourses = computed(() => {
    return Object.values(courseData.value)
      .flat()
      .filter((course) => course.selected);
  });

  const isOverlapping = (course: Course) => {
    const courseId = `${course.date}-${course.time}-${course.course_name}`;
    return overlappingIds.value.includes(courseId);
  };

  const getCoursesByCategory = (
    category: CourseCategory,
    day: DayFilter = selectedDay.value
  ) => {
    if (category === 'my day') {
      return Object.values(courseData.value)
        .flat()
        .filter((course) => course.selected && course.date === getDayDate(day))
        .sort((a, b) => a.time.localeCompare(b.time));
    }

    return (courseData.value[category] || [])
      .filter((course) => course.date === getDayDate(day))
      .sort((a, b) => a.time.localeCompare(b.time));
  };

  // Function to update roomModalEnabled from API
  function setRoomModalEnabled(enabled: boolean) {
    roomModalEnabled.value = enabled;
  }

  // Function to set maintenance mode
  function setMaintenanceMode(enabled: boolean, message?: string) {
    maintenanceMode.value = enabled;
    if (message) {
      maintenanceMessage.value = message;
    }
  }

  // We're no longer initializing courses here
  // Instead, we'll do it in the App.vue onMounted hook

  return {
    courseData,
    selectedCategory,
    selectedDay,
    menuOpen,
    pastCourses,
    isLoading,
    error,
    roomModalEnabled,
    maintenanceMode,
    maintenanceMessage,
    getCoursesByCategory,
    toggleCourseSelection,
    getSelectedCoursesCount,
    getSelectedCoursesCountForDay,
    fetchAndUpdateCourses,
    isOverlapping,
    setRoomModalEnabled,
    setMaintenanceMode,
    showConductors,
  };
});